// Core data types for FormMan

// Template source tracking for local-first architecture
export type TemplateSourceType = 'local' | 'gdrive' | 'url'

export interface TemplateSource {
  type: TemplateSourceType
  remoteId?: string // Google Drive file ID
  remotePath?: string // Original path/folder in remote
  remoteModified?: string // ISO timestamp of remote file's last modified
  importedAt?: string // ISO timestamp when first imported locally
  lastSyncedAt?: string // ISO timestamp when last synced back to remote
}

export interface Template {
  id: string
  clientName: string
  programName: string
  fileName: string
  fileSize: number
  pageCount: number
  uploadDate: Date
  lastModified: Date
  status: TemplateStatus
  pdfData: ArrayBuffer
  pdfinfo?: PDFInfo // PDF file information from zpaper/pdfinfo namespace
  dnaImage?: string // Miniature visual fingerprint (data URL) for template matching
  metadata?: TemplateMetadata // Deprecated: kept for backward compatibility
  configuration?: TemplateConfiguration
  stampzConfig?: StampzConfig
  shlinkConfig?: SHLinkConfig
  stickers?: ERxSticker[] // eRx stickers from zpaper/stickers namespace
  fields?: FieldDefinition[] // Field definitions with role assignments from zpaper/fields namespace
  // Workflow tracking
  currentStage: WorkflowStage
  workflowStages: WorkflowStageInfo[]
  // Deprecated - kept for backward compatibility during migration
  completedStages?: WorkflowStage[]
  skippedStages?: WorkflowStage[]
  // User tracking
  createdBy: string
  assignedTo: string
  // PDF analysis status (local templates only)
  analysisStatus?: 'none' | 'processing' | 'complete'
  analysisSummary?: { fieldCount: number, roles: Record<string, number>, formTitle?: string | null } | null
  // Unified source tracking (local-first architecture)
  source?: TemplateSource
  // DEPRECATED: Legacy fields kept for backward compatibility - use `source` instead
  isGoogleDrive?: boolean
  googleDriveId?: string
  googleDrivePath?: string
}

export type QRCodeSize = 'tiny' | 'small' | 'medium' | 'large' | 'huge'

export type StampzTextPlacement = 'inside' | 'above' | 'below' | 'left' | 'right' | 'none'

export interface StampzLocation {
  x: number // PDF user units from the left edge of the page
  y: number // PDF user units from the TOP edge of the page (increases downward, screen-style)
}

export interface StampzConfig {
  shortUrl: string
  fullUrl: string
  page: number // 1-based page number (0 = cover page)
  location: StampzLocation
  pdfY?: number // PDF-native y from page BOTTOM — for clients using pdf-lib / PDF.js drawImage; server-computed, not required in outgoing payloads
  size: Exclude<QRCodeSize, 'huge'>
  sizePx: number // resolved pixel size — clients use this directly
  textPlacement: StampzTextPlacement
}

export interface SHLinkConfig {
  page: number // 0 = generated cover sheet, 1+ = existing PDF page
  location: StampzLocation
  pdfY?: number // PDF-native y from page BOTTOM; -1 when page=0 (cover sheet); server-computed, not required in outgoing payloads
  size: QRCodeSize // named size preset (fixed to 'huge' for now)
  sizePx: number // resolved pixel size — clients use this directly
}

export enum TemplateStatus {
  DRAFT = 'draft',
  UPLOADED = 'uploaded',
  IN_PROGRESS = 'in_progress',
  CONFIGURED = 'configured',
  REVIEWED = 'reviewed',
  EXPORTED = 'exported',
  ARCHIVED = 'archived',
}

export interface PDFInfo {
  fileSize: number
  pageCount: number
  pdfVersion: string
  hasAcroFields: boolean
  isEncrypted: boolean
  producer: string
  creationDate: string
  uploadDate: string
  lastModified: string
  pageWidth: number
  pageHeight: number
  orientation: 'portrait' | 'landscape'
}

// Deprecated: kept for backward compatibility
export interface TemplateMetadata {
  pdfVersion: string
  hasAcroFields: boolean
  isEncrypted: boolean
  producer: string
  creationDate: Date
}

export interface ProgramStamp {
  id: string
  templateId: string
  qrData: string
  shortUrl: string
  fullUrl: string
  position: StampPosition
  size: StampSize
  page: number
  branding?: StampBranding
  dateAdded: Date
  mintRegistrationId?: string
  isVisible: boolean
}

export interface StampPosition {
  x: number
  y: number
  unit: 'points' | 'inches' | 'mm'
}

export interface StampSize {
  width: number
  height: number
  unit: 'points' | 'inches' | 'mm'
}

export interface StampBranding {
  logo?: string
  text?: string
  backgroundColor?: string
}

export type PharmacyService = 'photon' | 'surescripts'
export type DeclarationPlacement = 'top' | 'bottom' | 'left' | 'right'

export type AutofillType =
  | 'name'
  | 'given-name'
  | 'family-name'
  | 'bday'
  | 'bday-day'
  | 'bday-month'
  | 'bday-year'
  | 'tel'
  | 'tel-area-code'
  | 'tel-local'
  | 'email'
  | 'street-address'
  | 'postal-code'

export interface StickerDeclaration {
  full: string
  short: string
  placement: DeclarationPlacement
}

export interface SignatureMarker {
  id: string
  type: 'erx' | 'p11'
  field?: string
  top: number
  left: number
  width: number
  height: number
  page: number
}

export interface StickerPrescription {
  pharmacyId: string
  pharmacyService: string
  value: Record<string, unknown>
}

export interface ERxSticker {
  id?: string // internal UI tracking
  templateId?: string // internal
  page: number
  type: 'erx' | 'p11'
  label?: string // internal UI display
  bounds: StickerBounds
  required: boolean
  declaration: StickerDeclaration
  identityFields?: Record<string, string[]>
  prescription?: StickerPrescription
  signatures?: SignatureMarker[]
}

export interface StickerBounds {
  top: number // Integer pixel position from top
  left: number // Integer pixel position from left
  width: number // Integer pixel width
  height: number // Integer pixel height
}

export interface FieldDefinition {
  name: string
  page: number
  type: string // Tx, Btn, Sig, Ch
  autocomplete?: string
  required?: boolean
  value?: string // PHI value (dataDoc only, not templates)
  validation?: { regex: string; errorMessage: string }
  assignments: string[] // role IDs (empty = any role)
  mappings: {
    default: string
    initial?: string
    touched?: boolean // true once the admin has cleared or explicitly typed a value — freezes the field, suggestions never shown again until the Auto-suggest button is used
    suggested?: boolean // true when `initial` is known to have been written by the suggestion engine (saved while untouched and auto-filled) — the ONLY thing that lets a live suggestion overwrite non-empty content; absent/false means `initial` is of unknown origin (hand-authored, legacy, set outside this UI) and must never be silently replaced
  }
  linkId?: string // doc.js suggestedName — friendly-name hint, not applied automatically
  newName?: string // export-time rename target; PDF's AcroForm field name is untouched until export
}

export interface MobileConfig {
  all: string
  [role: string]: string // role → filename (e.g., 'patient.html')
}

export interface FieldAssignment {
  fieldName: string
  fieldType: FieldType
  role: UserRole
  page: number
  isRequired: boolean
  validation?: FieldValidation
  defaultValue?: any
}

export enum FieldType {
  TEXT = 'text',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  DROPDOWN = 'dropdown',
  SIGNATURE = 'signature',
  DATE = 'date',
}

export enum UserRole {
  PATIENT = 'patient',
  PRESCRIBER = 'prescriber',
  CAREGIVER = 'caregiver',
  ADMIN = 'admin',
  ERX = 'erx',
}

export interface FieldValidation {
  pattern?: string
  minLength?: number
  maxLength?: number
  required?: boolean
  customMessage?: string
}

export interface TemplateConfiguration {
  id: string
  templateId: string
  version: number
  programStamp?: ProgramStamp
  erxStickers: ERxSticker[]
  fieldAssignments: FieldAssignment[]
  createdBy: string
  createdAt: Date
  exportedAt?: Date
  notes?: string
}

export interface ExportPackage {
  template: Template
  configuration: TemplateConfiguration
  exportFormat: ExportFormat
  exportedPdf: ArrayBuffer
  configJson: string
  timestamp: Date
  checksum: string
}

export enum ExportFormat {
  PDF_WITH_METADATA = 'pdf_with_metadata',
  PDF_PLUS_JSON = 'pdf_plus_json',
  PACKAGE_ZIP = 'package_zip',
}

// API Response types
export interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
  timestamp: Date
}

export interface ApiError {
  success: false
  error: {
    code: string
    message: string
    details?: any
    timestamp: Date
  }
}

// UI State types
export interface AppState {
  currentTemplate: Template | null
  configuration: Partial<TemplateConfiguration>
  workflowStage: WorkflowStage
  validationErrors: ValidationError[]
  isDirty: boolean
  history: HistoryEntry[]
}

export enum WorkflowStage {
  UPLOAD = 'upload',
  MOBILE = 'mobile',
  STAMPZ = 'stampz',
  REGISTER = 'register',
  STICKERS = 'stickers',
  FIELDS = 'fields',
  MAPPING = 'mapping',
  REVIEW = 'review',
  EXPORT = 'export',
}

export enum StageStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
}

export interface WorkflowStageInfo {
  stage: WorkflowStage
  status: StageStatus
  startedAt?: string
  completedAt?: string
  skippedAt?: string
  notes?: string
}

export interface ValidationError {
  field: string
  message: string
  severity: 'error' | 'warning'
}

export interface HistoryEntry {
  action: string
  timestamp: Date
  state: Partial<AppState>
}

// UI Component Props — overlays now provided via <slot> in Vue
export interface PDFViewerProps {
  pdfData: ArrayBuffer
  currentPage: number
  zoom: number
  onPageChange: (page: number) => void
  onZoomChange: (zoom: number) => void
}

export interface QRGeneratorProps {
  data: string
  size: number
  errorLevel: 'L' | 'M' | 'Q' | 'H'
  onGenerate: (qrDataUrl: string) => void
}

export interface DragPosition {
  x: number
  y: number
}

export interface DragBounds {
  left: number
  top: number
  right: number
  bottom: number
}
